import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { Vessel, Trailer } from '../../models/types';
import { getVessels } from '../../firebase/services/vesselService';
import { getTrailers } from '../../firebase/services/trailerService';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';
import VesselRegistrationDialog from '../../components/VesselRegistrationDialog';
import TrailerRegistrationDialog from '../../components/TrailerRegistrationDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const RegistrationsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Registration Dialog States
  const [vesselRegistrationDialog, setVesselRegistrationDialog] = useState<{ open: boolean; vessel: Vessel | null }>({
    open: false,
    vessel: null
  });
  
  const [trailerRegistrationDialog, setTrailerRegistrationDialog] = useState<{ open: boolean; trailer: Trailer | null }>({
    open: false,
    trailer: null
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedVessels, fetchedTrailers] = await Promise.all([
          getVessels(),
          getTrailers()
        ]);
        
        setVessels(fetchedVessels);
        setTrailers(fetchedTrailers);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load registration data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleOpenVesselRegistration = (vessel: Vessel) => {
    setVesselRegistrationDialog({
      open: true,
      vessel
    });
  };
  
  const handleCloseVesselRegistration = () => {
    setVesselRegistrationDialog({
      open: false,
      vessel: null
    });
  };
  
  const handleOpenTrailerRegistration = (trailer: Trailer) => {
    setTrailerRegistrationDialog({
      open: true,
      trailer
    });
  };
  
  const handleCloseTrailerRegistration = () => {
    setTrailerRegistrationDialog({
      open: false,
      trailer: null
    });
  };
  
  const reloadData = async () => {
    try {
      setLoading(true);
      const [fetchedVessels, fetchedTrailers] = await Promise.all([
        getVessels(),
        getTrailers()
      ]);
      
      setVessels(fetchedVessels);
      setTrailers(fetchedTrailers);
    } catch (err) {
      console.error('Error reloading data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter vessels that have registration information
  const registeredVessels = vessels.filter(v => 
    v.propulsionType === 'powered' && v.registrationNumber
  );
  
  // Filter trailers that have registration information
  const registeredTrailers = trailers.filter(t => 
    t.stateRegistrationNumber
  );
  
  // Get vessels with registrations expiring within 30 days
  const today = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(today.getDate() + 30);
  
  const expiringSoonVessels = registeredVessels.filter(v => {
    if (!v.stateRegistrationExpiry) return false;
    const expiryDate = new Date(v.stateRegistrationExpiry);
    return expiryDate >= today && expiryDate <= thirtyDaysFromNow;
  });
  
  const expiringSoonTrailers = registeredTrailers.filter(t => {
    if (!t.stateRegistrationExpiry) return false;
    const expiryDate = new Date(t.stateRegistrationExpiry);
    return expiryDate >= today && expiryDate <= thirtyDaysFromNow;
  });
  
  const expiredVessels = registeredVessels.filter(v => {
    if (!v.stateRegistrationExpiry) return false;
    const expiryDate = new Date(v.stateRegistrationExpiry);
    return expiryDate < today;
  });
  
  const expiredTrailers = registeredTrailers.filter(t => {
    if (!t.stateRegistrationExpiry) return false;
    const expiryDate = new Date(t.stateRegistrationExpiry);
    return expiryDate < today;
  });
  
  // Helper to get status chip
  const getStatusChip = (expiryDate: string | undefined) => {
    if (!expiryDate) return <Chip size="small" label="No Expiry Date" color="error" />;
    
    const expiry = new Date(expiryDate);
    
    if (expiry < today) {
      return <Chip size="small" label="Expired" color="error" icon={<WarningIcon />} />;
    }
    
    if (expiry <= thirtyDaysFromNow) {
      return <Chip size="small" label="Expires Soon" color="warning" icon={<WarningIcon />} />;
    }
    
    return <Chip size="small" label="Valid" color="success" icon={<CheckCircleIcon />} />;
  };
  
  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Registration Management
        </Typography>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="registration tabs">
                <Tab label="Powered Vessels" />
                <Tab label="Trailers" />
              </Tabs>
            </Box>
            
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Powered Vessel Registrations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {expiringSoonVessels.length} expiring soon · {expiredVessels.length} expired
                </Typography>
              </Box>
              
              {registeredVessels.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vessel</TableCell>
                        <TableCell>Registration #</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {registeredVessels.map(vessel => (
                        <TableRow key={vessel.id} hover>
                          <TableCell>
                            <Typography variant="body2">{vessel.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {vessel.type.charAt(0).toUpperCase() + vessel.type.slice(1)}
                            </Typography>
                          </TableCell>
                          <TableCell>{vessel.registrationNumber}</TableCell>
                          <TableCell>{vessel.stateRegistrationState}</TableCell>
                          <TableCell>
                            {vessel.stateRegistrationExpiry ? 
                              new Date(vessel.stateRegistrationExpiry).toLocaleDateString() : 
                              'Not set'}
                          </TableCell>
                          <TableCell>
                            {getStatusChip(vessel.stateRegistrationExpiry)}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenVesselRegistration(vessel)}
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Vessel Registrations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You haven't added registration information for any powered vessels yet.
                  </Typography>
                </Paper>
              )}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Trailer Registrations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {expiringSoonTrailers.length} expiring soon · {expiredTrailers.length} expired
                </Typography>
              </Box>
              
              {registeredTrailers.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Trailer</TableCell>
                        <TableCell>License Plate</TableCell>
                        <TableCell>Registration #</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {registeredTrailers.map(trailer => (
                        <TableRow key={trailer.id} hover>
                          <TableCell>
                            <Typography variant="body2">{trailer.manufacturer} {trailer.model}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {trailer.year}
                            </Typography>
                          </TableCell>
                          <TableCell>{trailer.licensePlate}</TableCell>
                          <TableCell>{trailer.stateRegistrationNumber}</TableCell>
                          <TableCell>{trailer.stateRegistrationState}</TableCell>
                          <TableCell>
                            {trailer.stateRegistrationExpiry ? 
                              new Date(trailer.stateRegistrationExpiry).toLocaleDateString() : 
                              'Not set'}
                          </TableCell>
                          <TableCell>
                            {getStatusChip(trailer.stateRegistrationExpiry)}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenTrailerRegistration(trailer)}
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Trailer Registrations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You haven't added registration information for any trailers yet.
                  </Typography>
                </Paper>
              )}
            </TabPanel>
          </>
        )}
        
        <VesselRegistrationDialog
          open={vesselRegistrationDialog.open}
          onClose={handleCloseVesselRegistration}
          vessel={vesselRegistrationDialog.vessel}
          onUpdate={reloadData}
          onSubmit={async (vessel) => {
            await reloadData();
          }}
        />
        
        <TrailerRegistrationDialog
          open={trailerRegistrationDialog.open}
          onClose={handleCloseTrailerRegistration}
          trailer={trailerRegistrationDialog.trailer}
          onUpdate={reloadData}
        />
      </Box>
    </MarinaAdminLayout>
  );
};

export default RegistrationsPage; 